import React, { useState } from "react";
import { Upload, X, Loader2, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { api, absUrl } from "../../lib/api";
import { CATEGORIES } from "../../context/SettingsContext";
import { useToast } from "../../hooks/use-toast";

const blank = {
  name: "",
  price: "",
  compareAt: "",
  category: "tops",
  tags: [],
  images: [],
  sizes: ["S", "M", "L"],
  colors: [],
  description: "",
  soldOut: false,
};

// Predefined sizes the admin can toggle (in stock / not in stock)
const SIZE_OPTIONS = [
  "XS", "S", "M", "L", "XL", "XXL", "One Size",
  "34", "36", "38", "40", "42", "44",
];

export default function ProductForm({ initial, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ ...blank, ...initial });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#f5f0e6");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addColor = () => {
    const name = colorName.trim();
    if (!name) return;
    if ((form.colors || []).some((c) => c.name === name)) return;
    set("colors", [...(form.colors || []), { name, hex: colorHex }]);
    setColorName("");
  };

  const removeColor = (i) => set("colors", form.colors.filter((_, idx) => idx !== i));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const dataUrl = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(file);
        });
        const { url } = await api.uploadImage(dataUrl);
        setForm((f) => ({ ...f, images: [...f.images, url] }));
      }
      toast({ title: "Uploaded", description: "Image(s) added." });
    } catch (err) {
      toast({ title: "Upload failed", description: "Please try a smaller image." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const moveImage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.images.length) return;
    const arr = [...form.images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("images", arr);
  };

  const makePrimary = (i) => {
    if (i === 0) return;
    const arr = [...form.images];
    const [img] = arr.splice(i, 1);
    arr.unshift(img);
    set("images", arr);
  };

  const toggleTag = (tag) =>
    set("tags", form.tags.includes(tag) ? form.tags.filter((t) => t !== tag) : [...form.tags, tag]);

  const toggleSize = (size) =>
    set("sizes", form.sizes.includes(size) ? form.sizes.filter((s) => s !== size) : [...form.sizes, size]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) {
      toast({ title: "Add an image", description: "Please upload at least one product image." });
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      price: Number(form.price) || 0,
      compareAt: form.compareAt ? Number(form.compareAt) : null,
      category: form.category,
      tags: form.tags,
      images: form.images,
      sizes: form.sizes,
      colors: form.colors || [],
      soldOut: form.soldOut,
      description: form.description,
    };
    try {
      if (initial && initial.id) {
        await api.updateProduct(initial.id, payload);
      } else {
        await api.createProduct(payload);
      }
      toast({ title: "Saved", description: "Product saved successfully." });
      onSaved();
    } catch (err) {
      toast({ title: "Error", description: "Could not save product." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center overflow-y-auto p-4">
      <form onSubmit={submit} className="w-full max-w-2xl bg-white my-8">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4 sticky top-0 bg-white z-10">
          <h2 className="font-display text-xl tracking-wide">{initial && initial.id ? "Edit product" : "New product"}</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-black">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-2">IMAGES</label>
            <div className="flex flex-wrap gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative h-28 w-24 bg-neutral-100 overflow-hidden border border-neutral-200 rounded-md group">
                  <img src={absUrl(img)} alt="" className="h-full w-full object-cover" />

                  {/* primary label */}
                  {i === 0 && (
                    <span className="absolute top-1 left-1 bg-black text-white text-[9px] tracking-wide px-1.5 py-0.5 rounded">ראשית</span>
                  )}

                  {/* remove */}
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5" title="הסר">
                    <X className="h-3 w-3" />
                  </button>

                  {/* move / set primary controls */}
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 flex items-center justify-between px-1 py-1">
                    <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} className="text-white disabled:opacity-30" title="הזז שמאלה">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    {i !== 0 && (
                      <button type="button" onClick={() => makePrimary(i)} className="text-white hover:text-yellow-300" title="קבע כתמונה ראשית">
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1} className="text-white disabled:opacity-30" title="הזז ימינה">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <label className="h-28 w-24 border border-dashed border-neutral-300 rounded-md flex flex-col items-center justify-center cursor-pointer text-neutral-400 hover:border-black hover:text-black transition-colors">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                <span className="text-[10px] mt-1">{uploading ? "..." : "UPLOAD"}</span>
                <input type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" disabled={uploading} />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-neutral-400">התמונה הראשונה (מסומנת "ראשית") מוצגת בקטלוג. השתמש בחצים לסידור, או ב-⭐ כדי לקבוע תמונה כראשית.</p>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-1">NAME</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} required className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest text-neutral-500 mb-1">PRICE (₪)</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} required className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black" />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-neutral-500 mb-1">COMPARE AT</label>
              <input type="number" step="0.01" value={form.compareAt ?? ""} onChange={(e) => set("compareAt", e.target.value)} className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black" />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-1">CATEGORY</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black bg-white">
              {CATEGORIES.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-2">COLLECTIONS</label>
            <div className="flex gap-2">
              {["new-drop", "best-seller"].map((tag) => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)} className={`px-4 py-2 text-xs tracking-[0.15em] border transition-colors ${form.tags.includes(tag) ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600"}`}>
                  {tag.replace("-", " ").toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-2">SIZES IN STOCK</label>
            <div className="flex flex-wrap gap-2">
              {[...SIZE_OPTIONS, ...form.sizes.filter((s) => !SIZE_OPTIONS.includes(s))].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSize(s)}
                  className={`min-w-[44px] px-3 py-2 text-xs tracking-[0.1em] border transition-colors ${
                    form.sizes.includes(s)
                      ? "bg-black text-white border-black"
                      : "border-neutral-300 text-neutral-600 hover:border-black"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-neutral-400">סמן בלחיצה את המידות שיש במלאי (לחיצה שנייה מבטלת).</p>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-2">COLORS</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {(form.colors || []).map((c, i) => (
                <span key={i} data-testid={`color-chip-${i}`} className="inline-flex items-center gap-2 border border-neutral-300 pl-2 pr-1 py-1.5 text-xs">
                  <span className="h-4 w-4 rounded-full border border-neutral-300 shrink-0" style={{ backgroundColor: c.hex }} />
                  {c.name}
                  <button type="button" onClick={() => removeColor(i)} className="text-neutral-400 hover:text-black p-0.5" title="הסר צבע">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {(!form.colors || form.colors.length === 0) && (
                <span className="text-xs text-neutral-400">אין צבעים — המוצר יוצג ללא בחירת צבע</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={colorHex}
                onChange={(e) => setColorHex(e.target.value)}
                data-testid="color-hex-input"
                className="h-10 w-12 border border-neutral-300 cursor-pointer p-1 bg-white"
                title="בחר גוון"
              />
              <input
                value={colorName}
                onChange={(e) => setColorName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColor(); } }}
                placeholder="שם הצבע (למשל: שמנת)"
                data-testid="color-name-input"
                className="flex-1 border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black"
              />
              <button type="button" onClick={addColor} data-testid="add-color-btn" className="px-4 py-2.5 text-xs tracking-[0.15em] border border-neutral-300 hover:border-black transition-colors">
                הוסף
              </button>
            </div>
            <p className="mt-2 text-[11px] text-neutral-400">בחר גוון, כתוב שם צבע ולחץ "הוסף". הלקוחה תבחר צבע בעמוד המוצר והוא יופיע בהזמנת הוואטסאפ.</p>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-neutral-500 mb-1">DESCRIPTION</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black resize-none" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.soldOut} onChange={(e) => set("soldOut", e.target.checked)} />
            <span className="text-sm text-neutral-700">Mark as sold out</span>
          </label>
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs tracking-[0.2em] border border-neutral-300 hover:border-black transition-colors">CANCEL</button>
          <button type="submit" disabled={saving || uploading} className="px-6 py-2.5 text-xs tracking-[0.2em] bg-black text-white hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} SAVE
          </button>
        </div>
      </form>
    </div>
  );
}
